import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BOOK_URL } from "@/lib/booking";

const signatureEot = {
  n: "01",
  title: "Signature End of Tenancy · Deep Clean",
  tag: "Agency Standard",
  blurb:
    "The signature top-to-bottom sanitisation designed to satisfy the strictest inventory checks and guarantee the return of your deposit.",
  tiers: [
    { label: "Studio · 1 Bedroom", price: "from £195" },
    { label: "2 Bedroom · house or flat", price: "from £265" },
    { label: "3 Bedroom · house", price: "from £345" },
    { label: "4+ Bedroom · house", price: "from £445 · POA" },
  ],
  inclusions: [
    "Internal windows, frames and sills cleaned",
    "Deep oven, hob and extractor detail",
    "Limescale removal across all bathrooms",
    "Skirtings, switches and sockets wiped",
    "Letting-agent end-of-tenancy checklist met",
  ],
  footnote: "Carpet & upholstery extraction bundled at a preferred rate.",
};

const pricing = [
  {
    n: "02",
    title: "Deep Carpet & Upholstery Care",
    tag: "Hot-Water Extraction",
    blurb:
      "Advanced hot-water extraction and stain treatment — restoring pile, removing allergens, reviving high-traffic areas.",
    tiers: [
      { label: "Single · single bedroom", price: "£35" },
      { label: "Double room · living space", price: "£50" },
      { label: "Stairs & landing", price: "£45" },
      { label: "Minimum call-out", price: "£60" },
    ],
    note: "Minimum call-out waived when integrated with any Deep Clean package.",
  },
  {
    n: "03",
    title: "Handyman & Architectural Repairs",
    tag: "Precision Snagging",
    blurb:
      "Precision repairs, painting touch-ups and expert snagging to maintain your property's aesthetic and structural integrity.",
    tiers: [
      { label: "Initial hour · minimum", price: "£45" },
      { label: "Subsequent hourly rate", price: "£30" },
      { label: "Full day · 8 hours dedicated site work", price: "£220" },
    ],
    note: "Materials charged at cost. Quoted in writing before any work begins.",
  },
  {
    n: "04",
    title: "Garden & Outdoor Styling",
    tag: "Kerb Appeal",
    blurb:
      "Restoring kerb appeal and keeping exterior spaces pristine, structured and beautifully maintained.",
    tiers: [
      { label: "General detailing · lawn maintenance", price: "£40 / hour" },
      { label: "Garden clearance · rejuvenation", price: "from £55 / hour" },
    ],
    note: "Commercial green-waste disposal fees applied where relevant — quoted up front.",
  },
];

const signatureMoveIn = {
  n: "05",
  title: "Signature Move-In · Handover Package",
  tag: "The Flagship",
  blurb:
    "The ultimate turnkey experience. Designed for buyers, premium tenants and landlords who want a property looking, feeling and functioning like new from day one.",
  tiers: [{ label: "2-Bedroom property package", price: "from £280" }],
  inclusions: [
    "Comprehensive deep clean to agency standard",
    "Full mechanical and fixture checks",
    "Up to 1 hour of minor remedial repairs",
    "(hinges, bulbs, sealant touch-ups, anything that needs sorting)",
  ],
};

function PriceCard({ item, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: i * 0.05 }}
      className="border-2 border-[#0B1215] bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-12"
      data-testid={`price-card-${item.n}`}
    >
      <div className="md:col-span-5 p-8 lg:p-10 border-b-2 md:border-b-0 md:border-r-2 border-[#0B1215]">
        <div className="flex items-baseline gap-3 mb-4">
          <span className="font-mono-label text-[#FF5A36]">{item.n}</span>
          <span className="font-mono-label text-[#495057]">· {item.tag}</span>
        </div>
        <h3 className="font-display text-2xl lg:text-3xl tracking-tighter leading-tight text-[#0B1215]">
          {item.title}
        </h3>
        <p className="mt-4 text-sm text-[#495057] leading-relaxed">{item.blurb}</p>
      </div>

      <div className="md:col-span-7 p-8 lg:p-10 flex flex-col justify-between">
        <ul className="space-y-3" data-testid={`price-tiers-${item.n}`}>
          {item.tiers.map((t) => (
            <li
              key={t.label}
              className="flex items-baseline justify-between gap-4 pb-2 border-b border-dashed border-[#0B1215]/15"
            >
              <span className="text-[#0B1215] text-sm sm:text-base">{t.label}</span>
              <span className="font-display text-xl sm:text-2xl tracking-tight text-[#0B1215] shrink-0">
                {t.price}
              </span>
            </li>
          ))}
        </ul>
        {item.note && (
          <p className="mt-6 text-xs text-[#495057] italic leading-relaxed border-l-2 border-[#FF5A36] pl-3">
            {item.note}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function SignatureCard({ item, featuredLabel, ctaText, layout = "right" }) {
  const showTiersList = item.tiers.length > 1;
  const titleParts = item.title.split(" · ");
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6 }}
      className="bg-[#0B1215] text-[#F8F9FA] grid grid-cols-1 md:grid-cols-12 grain relative overflow-hidden"
      data-testid={`price-signature-${item.n}`}
    >
      <div className="absolute top-6 right-6 bg-[#FF5A36] text-[#F8F9FA] px-3 py-1.5 font-mono-label z-10">
        · {featuredLabel}
      </div>

      <div className="md:col-span-7 p-8 lg:p-12 relative z-10">
        <div className="flex items-baseline gap-3 mb-4">
          <span className="font-mono-label text-[#FF5A36]">{item.n}</span>
          <span className="font-mono-label text-[#F8F9FA]/60">· {item.tag}</span>
        </div>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
          {titleParts[0]}
          {titleParts[1] && (
            <span className="block italic font-light text-[#FF5A36] mt-2">
              · {titleParts[1]}
            </span>
          )}
        </h3>
        <p className="mt-6 text-[#F8F9FA]/75 leading-relaxed max-w-md">{item.blurb}</p>

        <div className="mt-8">
          <div className="font-mono-label text-[#FF5A36] mb-3">· INCLUDED AS STANDARD</div>
          <ul className="space-y-2">
            {item.inclusions.map((inc) => (
              <li key={inc} className="flex items-start gap-3 text-sm text-[#F8F9FA]/85">
                <span className="mt-2 w-1.5 h-1.5 bg-[#FF5A36] shrink-0" />
                <span>{inc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="md:col-span-5 p-8 lg:p-12 border-t-2 md:border-t-0 md:border-l-2 border-[#F8F9FA]/15 flex flex-col justify-between relative z-10">
        <div>
          {showTiersList ? (
            <>
              <div className="font-mono-label text-[#F8F9FA]/50 mb-4">· STARTING PRICES</div>
              <ul className="space-y-3">
                {item.tiers.map((t) => (
                  <li
                    key={t.label}
                    className="flex items-baseline justify-between gap-3 pb-2 border-b border-dashed border-[#F8F9FA]/15"
                  >
                    <span className="text-[#F8F9FA]/85 text-sm">{t.label}</span>
                    <span className="font-display text-lg sm:text-xl tracking-tight shrink-0">
                      {t.price}
                    </span>
                  </li>
                ))}
              </ul>
              {item.footnote && (
                <p className="text-xs text-[#F8F9FA]/55 italic mt-4 leading-relaxed">
                  {item.footnote}
                </p>
              )}
            </>
          ) : (
            <div>
              <div className="font-mono-label text-[#F8F9FA]/50 mb-2">{item.tiers[0].label}</div>
              <div className="font-display text-5xl sm:text-6xl tracking-tighter leading-none">
                {item.tiers[0].price.replace("from ", "")}
              </div>
              <div className="font-mono-label text-[#F8F9FA]/50 mt-2">· Starting price</div>
            </div>
          )}
        </div>
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 inline-flex items-center justify-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-6 py-4 font-mono-label active:scale-95 transition-all"
          data-testid={`price-signature-cta-${item.n}`}
        >
          {ctaText} <ArrowRight size={16} />
        </a>
      </div>
    </motion.div>
  );
}

export default function Pricing() {
  return (
    <section id="pricing" className="py-24 sm:py-32 bg-[#E9ECEF] border-y border-[#0B1215]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="font-mono-label text-[#FF5A36] mb-4">· SERVICES & INVESTMENT GUIDE</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B1215] tracking-tighter leading-[0.95] max-w-3xl">
              Honest pricing.<br />
              <span className="italic font-light">Quoted</span> per job.
            </h2>
          </div>
          <p className="text-[#495057] max-w-sm leading-relaxed">
            Two signature packages. Three à-la-carte services. Final quotes are fixed in writing
            within 24 hours of your request, with no hidden extras.
          </p>
        </div>

        <div className="space-y-5">
          <SignatureCard
            item={signatureEot}
            featuredLabel="SIGNATURE"
            ctaText="Request this clean"
          />
          {pricing.map((item, i) => (
            <PriceCard key={item.n} item={item} i={i} />
          ))}
          <SignatureCard
            item={signatureMoveIn}
            featuredLabel="FEATURED"
            ctaText="Request this package"
          />
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border-2 border-[#0B1215] bg-[#F8F9FA] p-8">
          <div>
            <div className="font-mono-label text-[#FF5A36] mb-2">· NOT QUITE STANDARD?</div>
            <p className="font-display text-2xl sm:text-3xl tracking-tight text-[#0B1215]">
              Most jobs are. <span className="italic font-light">Some aren't.</span>
            </p>
            <p className="text-sm text-[#495057] mt-2 max-w-md">
              Properties vary. Send a quick request with photos and I'll come back with a fixed,
              written quote within 24 hours.
            </p>
          </div>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0B1215] hover:bg-[#FF5A36] text-[#F8F9FA] px-6 py-4 font-mono-label active:scale-95 transition-all shrink-0"
            data-testid="pricing-cta"
          >
            Book a slot <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
